import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RefreshCw, CheckCircle, AlertTriangle, XCircle, Trash2, Activity, Server, Database, Cpu, HardDrive } from "lucide-react";

export default function AdminMonitoringTab({ apiUrl, getAuthHeaders }) {
  const credFetch = (url, opts = {}) => fetch(url, { ...opts, credentials: "include" });

  const [health, setHealth] = useState(null);
  const [errors, setErrors] = useState([]);
  const [uptime, setUptime] = useState(null);
  const [errorMeta, setErrorMeta] = useState({ total: 0, unresolved: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([loadHealth(), loadErrors(), loadUptime()]);
    setLoading(false);
  };

  const loadHealth = async () => {
    try {
      const res = await credFetch(`${apiUrl}/api/monitoring/health/detailed`);
      const data = await res.json();
      setHealth(data);
    } catch (e) { console.error("Health error:", e); }
  };

  const loadErrors = async () => {
    try {
      const res = await credFetch(`${apiUrl}/api/monitoring/errors?limit=30`);
      const data = await res.json();
      setErrors(data.errors || []);
      setErrorMeta({ total: data.total, unresolved: data.unresolved });
    } catch (e) { console.error("Errors fetch error:", e); }
  };

  const loadUptime = async () => {
    try {
      const res = await credFetch(`${apiUrl}/api/monitoring/uptime`);
      const data = await res.json();
      setUptime(data);
    } catch (e) { console.error("Uptime error:", e); }
  };

  const resolveAll = async () => {
    try {
      await credFetch(`${apiUrl}/api/monitoring/errors/resolve`, { method: "POST", headers: getAuthHeaders() });
      toast.success("All errors marked as resolved");
      loadErrors();
    } catch (e) { toast.error("Failed to resolve errors"); }
  };

  const clearOld = async () => {
    if (!window.confirm("Delete errors older than 30 days?")) return;
    try {
      await credFetch(`${apiUrl}/api/monitoring/errors/clear?days=30`, { method: "DELETE", headers: getAuthHeaders() });
      toast.success("Old errors cleared");
      loadErrors();
    } catch (e) { toast.error("Failed to clear errors"); }
  };

  const statusColor = (status) => status === "healthy" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700";
  const severityIcon = (type) => {
    if (type === "unhandled_exception") return <XCircle className="w-4 h-4 text-red-500" />;
    if (type === "server_error") return <AlertTriangle className="w-4 h-4 text-orange-500" />;
    return <Activity className="w-4 h-4 text-blue-500" />;
  };

  return (
    <div className="space-y-6" data-testid="admin-monitoring-tab">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Monitoring & Logs</h2>
        <Button variant="outline" onClick={loadAll} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />Refresh
        </Button>
      </div>

      {/* System Health */}
      {health && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className={`p-4 ${health.status === "healthy" ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
            <div className="flex items-center gap-3">
              {health.status === "healthy" ? <CheckCircle className="w-8 h-8 text-green-600" /> : <AlertTriangle className="w-8 h-8 text-red-600" />}
              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p className="font-bold capitalize">{health.status}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <Database className="w-8 h-8 text-violet-600" />
              <div>
                <p className="text-sm text-gray-500">Database</p>
                <p className="font-bold">{health.database.connected ? `${health.database.latency_ms}ms` : "Down"}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <Cpu className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-sm text-gray-500">CPU</p>
                <p className="font-bold">{health.system.cpu_percent}%</p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <HardDrive className="w-8 h-8 text-orange-600" />
              <div>
                <p className="text-sm text-gray-500">Memory</p>
                <p className="font-bold">{health.system.memory_used_percent}%</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Stats Row */}
      {health && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="p-4 text-center">
            <p className="text-3xl font-bold text-violet-600">{health.app.total_users}</p>
            <p className="text-sm text-gray-500">Total Users</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">{health.app.active_sessions}</p>
            <p className="text-sm text-gray-500">Active Sessions</p>
          </Card>
          <Card className="p-4 text-center">
            <p className={`text-3xl font-bold ${health.app.errors_24h > 0 ? "text-red-600" : "text-green-600"}`}>{health.app.errors_24h}</p>
            <p className="text-sm text-gray-500">Errors (24h)</p>
          </Card>
        </div>
      )}

      {/* Uptime */}
      {uptime && (
        <Card className="p-4">
          <h3 className="font-semibold mb-3">Uptime</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Last 24 Hours</p>
              <p className={`text-2xl font-bold ${uptime.uptime_24h >= 99 ? "text-green-600" : uptime.uptime_24h >= 95 ? "text-orange-600" : "text-red-600"}`}>
                {uptime.uptime_24h}%
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500">Last 7 Days</p>
              <p className={`text-2xl font-bold ${uptime.uptime_7d >= 99 ? "text-green-600" : uptime.uptime_7d >= 95 ? "text-orange-600" : "text-red-600"}`}>
                {uptime.uptime_7d}%
              </p>
            </div>
          </div>
          {uptime.error_breakdown?.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium mb-2">Error Breakdown (24h)</p>
              <div className="flex flex-wrap gap-2">
                {uptime.error_breakdown.map((e, i) => (
                  <Badge key={i} variant="outline" className="text-xs">{e.type}: {e.count}</Badge>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Error Logs */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg">Error Logs ({errorMeta.unresolved} unresolved)</CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={resolveAll}>
              <CheckCircle className="w-4 h-4 mr-1" /> Resolve All
            </Button>
            <Button size="sm" variant="outline" onClick={clearOld}>
              <Trash2 className="w-4 h-4 mr-1" /> Clear Old
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {errors.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckCircle className="w-12 h-12 mx-auto text-green-400 mb-2" />
              <p>No errors logged. System is running clean!</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {errors.map((err, i) => (
                <div key={i} className={`p-3 rounded-lg border-l-4 ${err.resolved ? "bg-gray-50 border-gray-300" : "bg-red-50 border-red-400"}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-2">
                      {severityIcon(err.type)}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-sm">{err.type}</span>
                          <Badge variant="outline" className="text-xs">{err.source}</Badge>
                          {err.resolved && <Badge className="bg-green-100 text-green-700 text-xs">Resolved</Badge>}
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{err.message}</p>
                        {err.details?.path && <p className="text-xs text-gray-400 mt-1">{err.details.method} {err.details.path}</p>}
                        {err.details?.url && <p className="text-xs text-gray-400 mt-1">{err.details.url}</p>}
                      </div>
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {err.timestamp ? new Date(err.timestamp).toLocaleString() : ""}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* System Details */}
      {health && (
        <Card className="p-4">
          <h3 className="font-semibold mb-3">System Details</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="flex justify-between p-2 bg-gray-50 rounded"><span className="text-gray-500">Python</span><span>{health.system.python_version}</span></div>
            <div className="flex justify-between p-2 bg-gray-50 rounded"><span className="text-gray-500">PID</span><span>{health.system.pid}</span></div>
            <div className="flex justify-between p-2 bg-gray-50 rounded"><span className="text-gray-500">Memory</span><span>{health.system.memory_used_gb} / {health.system.memory_total_gb} GB</span></div>
            <div className="flex justify-between p-2 bg-gray-50 rounded"><span className="text-gray-500">Disk</span><span>{health.system.disk_used_percent}%</span></div>
            <div className="flex justify-between p-2 bg-gray-50 rounded"><span className="text-gray-500">DB Latency</span><span>{health.database.latency_ms}ms</span></div>
            <div className="flex justify-between p-2 bg-gray-50 rounded"><span className="text-gray-500">Timestamp</span><span>{new Date(health.timestamp).toLocaleString()}</span></div>
          </div>
        </Card>
      )}
    </div>
  );
}
